import { useCallback, useState } from 'react';
import { readUnpaidReportOrders } from '../../helpers/unpaidReportOrders';
import {
  UnpaidReportOrders,
  UnpaidReportOrdersCode,
} from '../../types/unpaidReportOrders.types';

export type PendingUnpaidReportOrders = {
  orders: UnpaidReportOrders;
  /** Repeats the call that was refused, now with `confirmUnpaidOrders`. */
  confirm: () => void;
};

/**
 * Holds the unpaid report orders a disconnect or delete was refused over,
 * until the user confirms or cancels them in `UnpaidReportOrdersModal`.
 *
 * `intercept` goes first in the caller's catch. It answers true when the
 * failure was the confirmable 409 and the dialog is now open, so the caller
 * stays quiet; anything else, including the contact-support 409 the partner
 * and sales apps get, is the caller's to report as usual - that message is
 * the whole answer and the ordinary error toast shows it.
 */
export const useUnpaidReportOrders = () => {
  const [pending, setPending] = useState<PendingUnpaidReportOrders | null>(
    null
  );

  const intercept = useCallback((error: unknown, confirm: () => void) => {
    const orders = readUnpaidReportOrders(error);
    if (orders?.code !== UnpaidReportOrdersCode.confirm) return false;
    setPending({ orders, confirm });
    return true;
  }, []);

  const close = useCallback(() => setPending(null), []);

  return { pending, intercept, close };
};
